// The live channel of a gesture (SPEC-viewfinder §4.1, §4.2, ADR-0018).
//
// Rule 2 of §1.1 is absolute: a per-frame gesture calls its own `remeasure` and
// pushes **to itself**, never through `derived.subscribe`. `paint` is both the
// camera authority and a `derived` subscriber, so one push per frame would be
// one camera write per frame — `easeTo` restarting under the finger.
//
// „To itself" is three surfaces, though, and that is why this module exists
// rather than a closure in the wheel: the rail's distances (`src/axis/`), the
// toolbar's two counts (`src/axis/`) and the circle's dots and entering marks
// (`src/wheel/`). Those two folders may not import one another (ADR-0013), and
// the one place they may both reach is `src/state/`. So the wheel WRITES this
// and all three READ it, and `src/derived.ts` never hears of any of it: it
// stays neutral under a gesture, with no rAF, no timer and no camera.
//
// Nothing here writes the address, the history or this browser's memory. A
// gesture's one state write happens on settle, in `src/wheel/camera.ts`.
import type { Item } from '../shared/model.ts';

/**
 * What one frame of a gesture knows: distances and membership, and nothing
 * else. Never a new order and never a new clock (§4.1) — the order is settled
 * and the clock is `now` on `DerivedState`.
 *
 * The type lives here rather than in `src/derive/` because `state/` may not
 * import `derive/` at all, while `derive/` may name a `state/` type
 * (`src/structure.test.ts`, the `MAY` table). `remeasure` therefore returns
 * this shape by an `import type` of it.
 */
export type LiveMeasure = {
  /** Every row's distance from the live centre; `null` for a placeless row. */
  metres: Map<string, number | null>;
  /** The rows in reach — the positioned ones inside the radius, plus every placeless one. */
  inside: Set<string>;
  /** The positioned items inside the radius. „w promieniu N" while the finger is down. */
  placed: Item[];
  /** The positioned items outside it, nearest first: „poza M", and the dots (§3). */
  beyond: Item[];
};

type Listener = (live: LiveMeasure | null) => void;

let measured: LiveMeasure | null = null;
let live = false;
let justEnded = false;
const listeners = new Set<Listener>();

function announce() {
  const listening = [...listeners];
  for (const fn of listening) {
    if (!listeners.has(fn)) continue;
    try {
      fn(measured);
    } catch (e) {
      // One subscriber's mistake is its own — the stance every store in this
      // tree takes. A rail that throws may not freeze the circle beside it.
      console.error('gesture: a subscriber threw on a frame', e);
    }
  }
}

export const gesture = {
  /** A gesture has the map. From here until {@link end} nothing rebuilds. */
  begin() {
    live = true;
  },
  /** One frame's measurement, already throttled by the caller. */
  push(next: LiveMeasure) {
    if (!live) return;
    measured = next;
    announce();
  },
  /**
   * The camera is at rest. The settled derivation takes over, so the live
   * measurement is dropped and everyone is told once — which is what puts the
   * rows the gesture hid back under the derivation's own authority.
   *
   * `reordered` is the one-shot the FLIP reads (§4.2): a settle that moved the
   * centre re-orders a distance list, and that re-order animates. A poll does
   * not, which is why this is a flag the next render consumes rather than a
   * property of the render itself — the idiom `orderWasAsked()` already uses.
   */
  end(reordered = false) {
    if (!live) return;
    live = false;
    measured = null;
    justEnded = reordered;
    announce();
  },
  /** Whether a gesture owns the screen right now. A poll that lands here rebuilds nothing (§4.1). */
  active: (): boolean => live,
  /** The standing frame, for a consumer that mounts or redraws mid-gesture. */
  get: (): LiveMeasure | null => measured,
  /** Read once and cleared: the settle that has just happened re-ordered the list. */
  tookSettle(): boolean {
    const was = justEnded;
    justEnded = false;
    return was;
  },
  /** Called on every frame and on the settle. Returns the disposer, like every store here. */
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};
